/**
 * Explore Tabs Component - Navigation between Universities, Programs, Countries
 */

import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { GraduationCap, BookOpen, Globe, Grid3x3 } from 'lucide-react';

interface Tab {
  id: string;
  label: string;
  icon: React.ElementType;
  path: string;
  count?: number;
}

export const ExploreTabs: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const tabs: Tab[] = [
    { id: 'universities', label: 'Universities', icon: GraduationCap, path: '/explore/universities', count: 200 },
    { id: 'programs', label: 'Programs', icon: BookOpen, path: '/explore/programs', count: 86 },
    { id: 'countries', label: 'Countries', icon: Globe, path: '/explore/countries', count: 10 },
    { id: 'all', label: 'All Results', icon: Grid3x3, path: '/explore/search' },
  ];

  const getActiveTab = () => {
    const path = location.pathname;
    if (path.includes('/universities')) return 'universities';
    if (path.includes('/programs')) return 'programs';
    if (path.includes('/countries')) return 'countries';
    if (path.includes('/search')) return 'all';
    return 'universities';
  };

  const activeTab = getActiveTab();

  return (
    <div className="bg-card border-b border-border sticky top-16 z-40">
      <div className="container mx-auto px-6">
        <nav className="flex gap-2 overflow-x-auto">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;

            return (
              <button
                key={tab.id}
                onClick={() => navigate(tab.path + location.search)}
                className={`
                  flex items-center gap-2 px-6 py-4 border-b-2 transition-all whitespace-nowrap
                  ${isActive
                    ? 'border-primary text-primary font-semibold'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                  }
                `}
              >
                <Icon className="h-5 w-5" />
                <span>{tab.label}</span>
                {tab.count && (
                  <span className={`
                    px-2 py-0.5 rounded-full text-xs font-medium
                    ${isActive ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}
                  `}>
                    {tab.count}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </div>
    </div>
  );
};
